import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, X, Upload, Camera, Home } from 'lucide-react';
import { Layout } from '@/components/Layout';

interface PhotoSlot {
  id: string;
  label: string;
  file: File | null;
  preview: string | null;
  description?: string;
  isAdditional?: boolean;
}

const REQUIRED_SLOTS: Omit<PhotoSlot, 'file' | 'preview'>[] = [
  { id: 'front-exterior', label: 'Front exterior of house' },
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'great-room', label: 'Great Room/Family Room' },
  { id: 'master-bathroom', label: 'Primary bathroom' },
];

const OPTIONAL_SLOTS: Omit<PhotoSlot, 'file' | 'preview'>[] = [
  { id: 'rear-exterior', label: 'Rear exterior of house' },
  { id: 'backyard', label: 'Backyard' },
  { id: 'additional-exterior', label: 'Additional exterior (e.g., pool)' },
  { id: 'finished-basement', label: 'Finished basement (if applicable)' },
];

const MAX_ADDITIONAL_PHOTOS = 10;

export default function SubjectPropertyPhotos() {
  // Mock property address - in production this would come from context/props
  const propertyAddress = '123 Main Street, Austin, TX 78701';

  const [requiredPhotoSlots, setRequiredPhotoSlots] = useState<PhotoSlot[]>(
    REQUIRED_SLOTS.map((slot) => ({ ...slot, file: null, preview: null }))
  );
  
  const [optionalPhotoSlots, setOptionalPhotoSlots] = useState<PhotoSlot[]>(
    OPTIONAL_SLOTS.map((slot) => ({ ...slot, file: null, preview: null }))
  );
  
  const [additionalPhotos, setAdditionalPhotos] = useState<PhotoSlot[]>([]);

  const handleFileChange = (slotId: string, file: File | null, slotType: 'required' | 'optional' | 'additional') => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const updateFn = (prev: PhotoSlot[]) =>
        prev.map((slot) =>
          slot.id === slotId
            ? { ...slot, file, preview: reader.result as string }
            : slot
        );
      
      if (slotType === 'additional') {
        setAdditionalPhotos(updateFn);
      } else if (slotType === 'optional') {
        setOptionalPhotoSlots(updateFn);
      } else {
        setRequiredPhotoSlots(updateFn);
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (slotId: string, slotType: 'required' | 'optional' | 'additional') => {
    const updateFn = (prev: PhotoSlot[]) =>
      prev.map((slot) =>
        slot.id === slotId ? { ...slot, file: null, preview: null } : slot
      );
    
    if (slotType === 'additional') {
      setAdditionalPhotos(updateFn);
    } else if (slotType === 'optional') {
      setOptionalPhotoSlots(updateFn);
    } else {
      setRequiredPhotoSlots(updateFn);
    }
  };

  const addAdditionalPhoto = () => {
    if (additionalPhotos.length >= MAX_ADDITIONAL_PHOTOS) return;
    
    const newId = `additional-${Date.now()}`;
    setAdditionalPhotos((prev) => [
      ...prev,
      {
        id: newId,
        label: `Additional Photo ${prev.length + 1}`,
        file: null,
        preview: null,
        description: '',
        isAdditional: true,
      },
    ]);
  };

  const removeAdditionalPhotoSlot = (slotId: string) => {
    setAdditionalPhotos((prev) => prev.filter((slot) => slot.id !== slotId));
  };

  const updateAdditionalDescription = (slotId: string, description: string) => {
    setAdditionalPhotos((prev) =>
      prev.map((slot) =>
        slot.id === slotId ? { ...slot, description } : slot
      )
    );
  };

  const PhotoUploadSlot = ({
    slot,
    slotType,
    onRemoveSlot,
  }: {
    slot: PhotoSlot;
    slotType: 'required' | 'optional' | 'additional';
    onRemoveSlot?: () => void;
  }) => (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground">{slot.label}</Label>
          {slotType === 'required' && (
            <span className="text-xs text-destructive font-medium">(required)</span>
          )}
          {slotType === 'optional' && (
            <span className="text-xs text-muted-foreground">(optional)</span>
          )}
        </div>
        {slotType === 'additional' && onRemoveSlot && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemoveSlot}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {slot.preview ? (
        <div className="relative group">
          <img
            src={slot.preview}
            alt={slot.label}
            className="w-full h-40 object-cover rounded-lg border border-border"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => removePhoto(slot.id, slotType)}
            >
              <X className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors">
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Camera className="w-8 h-8 mb-2 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Click to upload</p>
          </div>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileChange(slot.id, file, slotType);
            }}
          />
        </label>
      )}

      {slotType === 'additional' && (
        <Textarea
          placeholder="Enter description for this photo..."
          className="mt-2 text-sm"
          value={slot.description || ''}
          onChange={(e) => updateAdditionalDescription(slot.id, e.target.value)}
        />
      )}
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Upload Photos Of Your Home
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Drop in a few photos of your place—our AI will do the heavy lifting and show how it measures up to comparable homes. Don't worry—you're in control of the final pricing adjustments and will have a chance to review and edit the report.
            </p>
          </div>

          {/* Property Address Card */}
          <Card className="mb-8">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Home className="h-5 w-5 text-primary" />
                Property Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground font-medium">{propertyAddress}</p>
            </CardContent>
          </Card>

          {/* Required Photos */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                Required Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {requiredPhotoSlots.map((slot) => (
                  <PhotoUploadSlot key={slot.id} slot={slot} slotType="required" />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Optional Photos */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-muted-foreground" />
                Optional Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {optionalPhotoSlots.map((slot) => (
                  <PhotoUploadSlot key={slot.id} slot={slot} slotType="optional" />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Photos */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary" />
                  Additional Photos
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addAdditionalPhoto}
                  disabled={additionalPhotos.length >= MAX_ADDITIONAL_PHOTOS}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Photo ({additionalPhotos.length}/{MAX_ADDITIONAL_PHOTOS})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {additionalPhotos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Click the button above to add additional photos</p>
                  <p className="text-sm">You can add up to {MAX_ADDITIONAL_PHOTOS} additional photos</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {additionalPhotos.map((slot) => (
                    <PhotoUploadSlot
                      key={slot.id}
                      slot={slot}
                      slotType="additional"
                      onRemoveSlot={() => removeAdditionalPhotoSlot(slot.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button size="lg" className="px-8">
              Save & Continue
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
